/**
 * FigDragLayer — 서버가 그려 준 도판 위에 손잡이를 얹어 손으로 옮기게 한다.
 *
 * 좌표를 숫자로만 고치면 「이 점을 조금 왼쪽으로」 같은 일이 몇 번의 시행착오가 된다.
 * 그림 위에서 바로 끌면 한 번이다.
 *
 * ── 지키는 것 넷 ──
 * ① **변환을 검산하고 연다.** 픽셀↔수학 좌표 변환은 서버 조판을 역산한 값이라(figCoords.js)
 *    서버가 조판을 바꾸면 어긋난다. 명세의 점과 실제 그려진 자리를 대조해 2px 넘게 벌어지면
 *    끌기를 열지 않는다 — 어긋난 채 열면 끌수록 그림이 나빠진다.
 *    **판정은 이 파일이 하지 않는다**(2026-09-06): figCoords.figDragState 가 정하고 이 층은 받아 그린다.
 *    열리지 않는 까닭도 여기서 말하지 않는다 — 종전에는 그림 위에 겹쳐 띄워 도판을 가렸고, 이제
 *    미리보기 칸의 머리줄이 말한다(figureText.figDragNote · FigureModal). 그래서 이 컴포넌트는
 *    「열릴 때만 손잡이를 그리는」 한 가지 일만 한다.
 * ② **끄는 동안에는 서버를 부르지 않는다.** 손잡이만 따라 움직이고, 손을 뗄 때 한 번 반영한다.
 *    움직일 때마다 부르면 초당 수십 번이 되고, 늦게 온 응답이 화면을 되돌린다.
 * ③ **범위 밖으로 못 나간다.** 나가면 그림에서 잘려 사라지는데 값은 바뀌어 있어, 화면과
 *    명세가 어긋난 채로 저장된다.
 * ④ **키보드로도 옮긴다.** 손잡이에 초점을 두고 화살표 — 마우스가 없으면 못 쓰는 기능이
 *    되지 않게. Shift 를 누르면 눈금의 5배로 크게 움직인다.
 */
import { useRef, useState } from 'react';
import {
  snapTo, clamp, applyHandle, applyCurveLabelHandle,
} from './figCoords';

const R = 6;          // 손잡이 반지름(px, 도판 좌표계)
const R_ACTIVE = 8;

/**
 * @param state figCoords.figDragState 의 반환값 — 부모가 한 번 재서 넘긴다(머리줄 문구와 같은 값을 읽는다).
 *   `reason` 이 있으면 열리지 않는 상태이므로 **아무것도 그리지 않는다**(까닭은 머리줄이 말한다).
 */
export default function FigDragLayer({ spec, state, onChange, step = 0.5, disabled = false }) {
  const svgRef = useRef(null);
  const [drag, setDrag] = useState(null);   // { id, x, y }
  const [hover, setHover] = useState(null);

  // 열리지 않는 상태 — 그림을 가리는 안내를 얹지 않는다(머리줄이 이미 그 까닭을 말하고 있다).
  if (!state || state.reason || !state.tr) return null;
  const { tr, handles } = state;

  const toData = (e) => {
    const rect = svgRef.current?.getBoundingClientRect();
    if (!rect || rect.width === 0 || rect.height === 0) return null;
    const px = (e.clientX - rect.left) * (tr.width / rect.width);
    const py = (e.clientY - rect.top) * (tr.height / rect.height);
    return tr.toData(px, py);
  };

  const bound = (h, d) => ({
    x: h.kx ? clamp(snapTo(d.x, step), tr.xRange[0], tr.xRange[1]) : h.x,
    // freeY — 세로를 적을 필드가 명세에 없는데도(곡선 이름표) 옮길 수는 있는 손잡이다.
    // 세로가 바뀌면 applyCurveLabelHandle 이 그 이름표를 자유 글자로 떼어낸다.
    y: (h.ky || h.freeY) ? clamp(snapTo(d.y, step), tr.yRange[0], tr.yRange[1]) : h.y,
  });

  // 손잡이 종류에 맞는 쓰기 — 곡선 이름표만 전용 경로다(세로로 옮기면 labels[] 로 옮겨 간다)
  const applyMove = (h, x, y) => (h.freeY ? applyCurveLabelHandle(spec, h, x, y) : applyHandle(spec, h, x, y));

  const onDown = (e, h) => {
    if (disabled) return;
    e.preventDefault();
    e.stopPropagation();
    e.currentTarget.setPointerCapture?.(e.pointerId);
    setDrag({ id: h.id, x: h.x, y: h.y });
  };

  const onMove = (e, h) => {
    if (!drag || drag.id !== h.id) return;
    const d = toData(e);
    if (!d) return;
    setDrag({ id: h.id, ...bound(h, d) }); // ② 화면만 따라 움직인다
  };

  const onUp = (e, h) => {
    if (!drag || drag.id !== h.id) return;
    e.currentTarget.releasePointerCapture?.(e.pointerId);
    const moved = drag.x !== h.x || drag.y !== h.y;
    setDrag(null);
    if (moved) onChange(applyMove(h, drag.x, drag.y)); // 손을 뗄 때 한 번만
  };

  const onKey = (e, h) => {
    if (disabled) return;
    const big = e.shiftKey ? 5 : 1;
    const s = (Number(step) > 0 ? Number(step) : 0.1) * big;
    const map = { ArrowLeft: [-s, 0], ArrowRight: [s, 0], ArrowUp: [0, s], ArrowDown: [0, -s] };
    const mv = map[e.key];
    if (!mv) return;
    e.preventDefault();
    const next = bound(h, { x: h.x + mv[0], y: h.y + mv[1] });
    if (next.x !== h.x || next.y !== h.y) onChange(applyMove(h, next.x, next.y));
  };

  return (
    <svg
      ref={svgRef}
      viewBox={`0 0 ${tr.width} ${tr.height}`}
      className="absolute inset-0 size-full"
      style={{ touchAction: 'none' }}
      aria-label="그림 위 손잡이"
    >
      {handles.map((h) => {
        const live = drag?.id === h.id ? drag : h;
        const p = tr.toPx(live.x, live.y);
        const active = drag?.id === h.id || hover === h.id;
        return (
          <g key={h.id}>
            {/* 잡기 쉬우라고 실제보다 넓은 투명 판을 덧댄다 — 보이는 원만으로는 6px 표적이다 */}
            <circle
              cx={p.px} cy={p.py} r={R_ACTIVE + 4}
              fill="transparent"
              tabIndex={disabled ? -1 : 0}
              role="button"
              aria-label={h.freeY
                ? `${h.name} — 화살표로 옮기기. 위아래로 옮기면 곡선에서 떼어 낸 글자가 됩니다`
                : `${h.name} — 화살표로 옮기기`}
              style={{ cursor: disabled ? 'default' : 'grab', outline: 'none' }}
              onPointerDown={(e) => onDown(e, h)}
              onPointerMove={(e) => onMove(e, h)}
              onPointerUp={(e) => onUp(e, h)}
              onPointerCancel={(e) => onUp(e, h)}
              onPointerEnter={() => setHover(h.id)}
              onPointerLeave={() => setHover((v) => (v === h.id ? null : v))}
              onFocus={() => setHover(h.id)}
              onBlur={() => setHover((v) => (v === h.id ? null : v))}
              onKeyDown={(e) => onKey(e, h)}
            />
            {/* 색은 저장소 토큰으로만 — 브랜드 초록(primary)과 먹색(headerBg)이다.
                SVG 라 클래스가 아니라 fill/stroke 유틸을 쓴다(Tailwind 3.4). */}
            <circle
              cx={p.px} cy={p.py} r={active ? R_ACTIVE : R}
              className="fill-primary stroke-white"
              fillOpacity={active ? 0.9 : 0.55}
              strokeWidth="1.5" pointerEvents="none"
            />
            {active && (
              <text
                x={p.px} y={p.py - R_ACTIVE - 5} textAnchor="middle"
                fontSize="10" fontWeight="700" className="fill-headerBg" pointerEvents="none"
              >
                {h.kx && h.ky ? `${live.x}, ${live.y}` : (h.kx ? `x=${live.x}` : `y=${live.y}`)}
              </text>
            )}
          </g>
        );
      })}
    </svg>
  );
}
